import {ActionIcon, Alert, Anchor, Button, Group, Stack, Text, Title} from '@mantine/core'
import {IconEdit} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'
import {useEffect, useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {OrderPlusButtons} from 'src/jobs/order-plus/order-plus-buttons'
import {pickAddJobSource} from 'src/jobs/order-plus/pick-source-job'
import {useProjectDeployedEquipment} from 'src/project-deployed-equipment/use-project-deployed-equipment'
import {AutoWidthTitleInput} from 'src/ui/auto-width-title-input'
import {ConfirmDialog} from 'src/ui/confirm-dialog'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'
import {PartialPickupBadge} from 'src/work/partial-pickup'
import {PickupSoonerButton} from 'src/work/pickup-sooner-button'

import {plannedJobsToCancel} from './planned-jobs-to-cancel'
import {ProjectStatusControl} from './project-status-control'
import type {ProjectDetail} from './schemas'
import {
    useApproveProject,
    useArchiveProject,
    usePatchProject,
    useReopenProject,
} from './use-projects'

type ProjectDetailsHeaderProps = {
    project: ProjectDetail
    canEdit: boolean
    readOnlyNote: string | null
}

function formatCreatedAt(createdAt: string) {
    return formatBusinessTimestamp(createdAt, 'MMM d, yyyy')
}

function countJobs(count: number, state: string) {
    return `${count} ${state} ${count === 1 ? 'job' : 'jobs'}`
}

function archiveConfirmationMessage(plannedCount: number, activeCount: number) {
    const outcomes = [
        ...(plannedCount > 0 ? [`cancel ${countJobs(plannedCount, 'estimate')}`] : []),
        ...(activeCount > 0 ? [`close ${countJobs(activeCount, 'active')}`] : []),
    ]
    return `Archiving this order will ${outcomes.join(' and ')}. Continue?`
}

export function ProjectDetailsHeader({project, canEdit, readOnlyNote}: ProjectDetailsHeaderProps) {
    const projectId = project.id
    const approveProject = useApproveProject(projectId)
    const archiveProject = useArchiveProject(projectId)
    const reopenProject = useReopenProject(projectId)
    const patchProject = usePatchProject(projectId)
    const {data: deployedEquipment} = useProjectDeployedEquipment(projectId)
    const equipmentOut =
        deployedEquipment != null &&
        deployedEquipment.stockRows.length +
            deployedEquipment.identifiedRows.length +
            deployedEquipment.thirdPartyRows.length >
            0
    const [actionError, setActionError] = useState<string | null>(null)
    const [isEditingName, setIsEditingName] = useState(false)
    const [projectName, setProjectName] = useState(project.name)
    const [nameError, setNameError] = useState<string | null>(null)
    const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false)
    const [plannedToCancel, setPlannedToCancel] = useState<number | null>(null)

    useEffect(() => {
        setProjectName(project.name)
    }, [project.id, project.name])

    async function runLifecycleAction(
        action: 'approve' | 'archive' | 'reopen',
        confirmCancelPlanned = false,
    ) {
        setActionError(null)
        try {
            if (action === 'approve') {
                await approveProject.mutateAsync()
            } else if (action === 'archive') {
                await archiveProject.mutateAsync(confirmCancelPlanned ? {confirmCancelPlanned} : {})
            } else {
                await reopenProject.mutateAsync()
            }
        } catch (lifecycleError) {
            const toCancel = action === 'archive' ? plannedJobsToCancel(lifecycleError) : null
            if (toCancel != null) {
                setPlannedToCancel(toCancel)
                return
            }
            setActionError(
                lifecycleError instanceof Error
                    ? lifecycleError.message
                    : 'Failed to update order status',
            )
        }
    }

    function selectArchive() {
        if (project.plannedCount + project.activeCount > 0 && !equipmentOut) {
            setActionError(null)
            setArchiveConfirmationOpen(true)
            return
        }
        void runLifecycleAction('archive')
    }

    async function confirmArchive() {
        setArchiveConfirmationOpen(false)
        await runLifecycleAction('archive', project.plannedCount > 0)
    }

    async function confirmCancelPlannedAndArchive() {
        setActionError(null)
        try {
            await archiveProject.mutateAsync({confirmCancelPlanned: true})
            setPlannedToCancel(null)
        } catch (archiveError) {
            setPlannedToCancel(null)
            setActionError(
                archiveError instanceof Error ? archiveError.message : 'Failed to archive order',
            )
        }
    }

    function startEditingName() {
        setProjectName(project.name)
        setNameError(null)
        setIsEditingName(true)
    }

    function cancelEditingName() {
        setProjectName(project.name)
        setNameError(null)
        setIsEditingName(false)
    }

    async function saveProjectName() {
        const trimmedName = projectName.trim()
        if (!trimmedName) {
            setNameError('Order name is required')
            return
        }

        setNameError(null)
        try {
            const updatedProject = await patchProject.mutateAsync({
                name: trimmedName,
                description: project.description,
                notes: project.notes,
                customerId: project.customerId,
                internalContactId: project.internalContact?.id ?? null,
                customerContactId: project.customerContact?.id ?? null,
                poNumber: project.poNumber,
            })
            setProjectName(updatedProject.name)
            setIsEditingName(false)
        } catch (saveFailure) {
            setNameError(
                saveFailure instanceof Error ? saveFailure.message : 'Failed to save order name',
            )
        }
    }

    const {sourceQuoteId} = project
    const lifecyclePending =
        approveProject.isPending || archiveProject.isPending || reopenProject.isPending

    return (
        <>
            <Stack gap={4}>
                <DetailPageBackLink to="/projects" listLabel="orders" />

                <Group justify="space-between" align="flex-start" wrap="wrap">
                    <Stack gap={4} style={{minWidth: 0, flex: 1}}>
                        <Group gap="sm" align="center" wrap="nowrap" style={{minWidth: 0}}>
                            {isEditingName ? (
                                <AutoWidthTitleInput
                                    value={projectName}
                                    onChange={(event) => setProjectName(event.currentTarget.value)}
                                    ariaLabel="Order name"
                                />
                            ) : (
                                <Title order={1} style={{minWidth: 0}}>
                                    {projectName}
                                </Title>
                            )}

                            {!canEdit ? null : !isEditingName ? (
                                <ActionIcon
                                    variant="subtle"
                                    size="lg"
                                    aria-label="Edit order name"
                                    onClick={startEditingName}
                                >
                                    <IconEdit size={20} stroke={1.5} />
                                </ActionIcon>
                            ) : (
                                <Group gap="xs" wrap="nowrap">
                                    <Button
                                        variant="default"
                                        size="compact-sm"
                                        onClick={cancelEditingName}
                                        disabled={patchProject.isPending}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="compact-sm"
                                        onClick={() => void saveProjectName()}
                                        loading={patchProject.isPending}
                                    >
                                        Save
                                    </Button>
                                </Group>
                            )}
                        </Group>
                        {nameError ? (
                            <Text size="sm" c="red">
                                {nameError}
                            </Text>
                        ) : null}
                        <Text size="sm" c="dimmed">
                            <Anchor
                                size="sm"
                                renderRoot={(props) => (
                                    <Link
                                        {...props}
                                        to="/customers/$customerId"
                                        params={{customerId: project.customerId}}
                                    />
                                )}
                            >
                                {project.customerName}
                            </Anchor>
                            {' · '}Created {formatCreatedAt(project.createdAt)}
                            {sourceQuoteId ? (
                                <>
                                    {' · '}
                                    <Anchor
                                        size="sm"
                                        renderRoot={(props) => (
                                            <Link
                                                {...props}
                                                to="/quotes/$quoteId"
                                                params={{quoteId: sourceQuoteId}}
                                            />
                                        )}
                                    >
                                        Source quote
                                    </Anchor>
                                </>
                            ) : null}
                            {' · '}
                            {project.jobs.length} {project.jobs.length === 1 ? 'job' : 'jobs'}
                            {project.orderNumber ? ` · Order ${project.orderNumber}` : ''}
                            {project.schedules.length > 0
                                ? ` · ${project.schedules.length} ${
                                      project.schedules.length === 1 ? 'schedule' : 'schedules'
                                  }`
                                : ''}
                        </Text>
                    </Stack>

                    <Group gap="sm" align="center">
                        {canEdit ? (
                            <>
                                {project.status !== 'closed' ? (
                                    <OrderPlusButtons
                                        projectId={projectId}
                                        sourceJobId={pickAddJobSource(project.jobs)?.id ?? null}
                                        pickupFromJobId={null}
                                    />
                                ) : null}
                                <PickupSoonerButton projectId={projectId} fromJobId={null} />
                            </>
                        ) : null}
                        {canEdit ? (
                            <ProjectStatusControl
                                status={project.status}
                                pending={lifecyclePending}
                                onApprove={() => void runLifecycleAction('approve')}
                                onArchive={selectArchive}
                                onReopen={() => void runLifecycleAction('reopen')}
                            />
                        ) : null}
                    </Group>
                </Group>
            </Stack>

            <PartialPickupBadge partialPickup={project.partialPickup} />

            {readOnlyNote ? (
                <Alert color="gray" variant="light">
                    {readOnlyNote}
                </Alert>
            ) : null}

            {actionError && (
                <Alert color="red" title="Could not update order">
                    {actionError}
                </Alert>
            )}

            <ConfirmDialog
                opened={archiveConfirmationOpen}
                title="Archive order?"
                message={archiveConfirmationMessage(project.plannedCount, project.activeCount)}
                confirmLabel="Archive order"
                cancelLabel="Keep order"
                loading={archiveProject.isPending}
                onConfirm={() => void confirmArchive()}
                onCancel={() => setArchiveConfirmationOpen(false)}
            />

            <ConfirmDialog
                opened={plannedToCancel != null}
                title="Cancel jobs and archive?"
                message={`This will cancel ${countJobs(plannedToCancel ?? 0, 'estimate')}. Archive anyway?`}
                confirmLabel="Archive anyway"
                cancelLabel="Keep order"
                loading={archiveProject.isPending}
                onConfirm={() => void confirmCancelPlannedAndArchive()}
                onCancel={() => setPlannedToCancel(null)}
            />
        </>
    )
}
